import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { FieldType } from '@lt/widget/components/Cigarette/types';

import { DEFAULT_TOPBAR_SLIDE } from 'src/constants';

import { initAppAction } from 'src/store/view/actions';
import { getViewInitialState } from 'src/store/view/reducer';

import { clearPreviousPackageDataAction } from '../actions/package';
import { changeFlightClassAction } from '../../CheckoutRoutes/PackageCheckout/store/flights/slice';

import type { ViewStateType } from '../view/types';

const initialState: ViewStateType = {
  ...getViewInitialState(),
  activeTouristForm: 0,
};

const viewSlice = createSlice({
  name: 'view',
  initialState,
  reducers: {
    toggleMap: (state) => ({
      ...state,
      isMapExpanded: !state.isMapExpanded,
    }),
    routeTransitionStart: (state) => ({
      ...state,
      isRouteTransition: true,
    }),
    routeTransitionEnd: (state) => ({
      ...state,
      isRouteTransition: false,
    }),
    expandTouristForm: (state, { payload }) => ({
      ...state,
      activeTouristForm: payload,
    }),
    setTopbarIndex: (state, { payload }) => ({
      ...state,
      topbarIndex: payload,
    }),
    toggleTopbar: (state, { payload }) => ({
      ...state,
      isTopbarExpanded: payload,
    }),
    setScrollPosition: (state, { payload }) => ({
      ...state,
      scrollPosition: payload,
    }),
    resetScrollPosition: (state) => ({
      ...state,
      scrollPosition: null,
    }),
    toggleFiltersPanel: (state) => ({
      ...state,
      showFiltersModal: !state.showFiltersModal,
    }),
    setInitialOpenedField: (
      state,
      { payload }: PayloadAction<FieldType | undefined>,
    ) => ({
      ...state,
      initialOpenedField: payload,
    }),
    setFlightsProgressBarIsSticky: (
      state,
      { payload }: PayloadAction<boolean>,
    ) => ({
      ...state,
      flightsProgressBarIsSticky: payload,
    }),
    setShowSummary: (state, { payload }: PayloadAction<boolean>) => ({
      ...state,
      showSummary: payload,
    }),
    shrinkTouristForms: (state) => ({
      ...state,
      activeTouristForm: 0,
    }),
  },
  extraReducers: (builder) =>
    builder
      .addCase(initAppAction, (state, { payload }) => {
        const { appName } = payload;
        return { ...state, app: appName };
      })
      .addCase(changeFlightClassAction, (state, { payload }) => ({
        ...state,
        selectedClass: payload,
      }))
      .addCase(clearPreviousPackageDataAction, (state) => ({
        ...state,
        isTopbarExpanded: false,
        activeTouristForm: 0,
        topbarIndex: DEFAULT_TOPBAR_SLIDE,
      })),
});

export default viewSlice.reducer;
export const {
  toggleMap,
  routeTransitionStart,
  routeTransitionEnd,
  expandTouristForm,
  setTopbarIndex,
  toggleTopbar,
  setScrollPosition,
  resetScrollPosition,
  toggleFiltersPanel,
  setInitialOpenedField,
  setFlightsProgressBarIsSticky,
  setShowSummary,
  shrinkTouristForms,
} = viewSlice.actions;
